<template>
  <div>
    <div class="mb-6">
      <h1 class="text-h5 font-weight-bold mb-1">Registrar visita</h1>
      <p class="text-body-2 text-muted mb-0" style="max-width: 480px">
        Busca al miembro por nombre o RUT para registrar su ingreso o renovar
        su membresía.
      </p>
    </div>

    <!-- ESTADO: BUSQUEDA -->
    <v-card
      v-if="!miembro"
      class="mx-auto"
      max-width="640"
      rounded="lg"
      elevation="2"
    >
      <v-card-text class="text-center py-10 px-8">
        <v-avatar size="58" color="primary" variant="tonal" class="mb-4">
          <v-icon size="26">mdi-magnify</v-icon>
        </v-avatar>
        <div class="text-h6 font-weight-bold mb-2">Busca un miembro</div>
        <p class="text-body-2 text-muted mb-6">
          Escribe el nombre o RUT para abrir su ficha y registrar la visita.
        </p>

        <busqueda-miembro @seleccionado="onMiembroSeleccionado" />

        <div class="d-flex align-center my-6" style="gap: 12px">
          <v-divider></v-divider>
          <span class="text-caption text-muted">o bien</span>
          <v-divider></v-divider>
        </div>

        <v-btn
          variant="outlined"
          color="indigo"
          block
          size="large"
          @click="mostrarDialogoRegular = true"
        >
          <v-icon start>mdi-check-circle-outline</v-icon>
          Registrar visita regular
        </v-btn>
      </v-card-text>
    </v-card>

    <!-- ESTADO: MIEMBRO ENCONTRADO -->
    <div v-else class="mx-auto" style="max-width: 760px">
      <v-btn variant="text" color="muted" class="mb-2" @click="miembro = null">
        <v-icon start size="18">mdi-arrow-left</v-icon>
        Buscar otro miembro
      </v-btn>

      <v-card rounded="lg" elevation="3" class="d-flex flex-column flex-sm-row overflow-hidden">
        <div
          class="d-flex align-center justify-center"
          style="width: 220px; min-height: 180px; background: linear-gradient(160deg, #5a93be, #345f82)"
        >
          <v-avatar size="96" v-if="miembro.imagen">
            <v-img :src="urlImagen(miembro.imagen)" cover></v-img>
          </v-avatar>
          <v-icon v-else size="72" color="#EAF2F8">mdi-account</v-icon>
        </div>

        <div class="flex-grow-1 pa-6 d-flex flex-column" style="gap: 18px">
          <div class="d-flex align-start justify-space-between" style="gap: 16px">
            <div>
              <div class="text-h6 font-weight-bold">{{ miembro.nombre }}</div>
              <div class="d-flex flex-wrap text-body-2 text-muted mt-1" style="gap: 20px">
                <span class="d-flex align-center" style="gap: 6px">
                  <v-icon size="16">mdi-card-account-details-outline</v-icon>
                  RUT: {{ miembro.telefono }}
                </span>
                <span class="d-flex align-center" style="gap: 6px">
                  <v-icon size="16">mdi-identifier</v-icon>
                  Matrícula: {{ miembro.matricula }}
                </span>
              </div>
            </div>
            <v-chip :color="estadoColor" variant="tonal" size="small" class="font-weight-bold">
              {{ estadoTexto }}
            </v-chip>
          </div>

          <v-card
            v-if="miembro.membresia"
            color="primary"
            variant="tonal"
            rounded="lg"
            class="d-flex align-center pa-3"
            style="gap: 12px"
          >
            <v-icon color="primary">mdi-wallet-membership</v-icon>
            <div>
              <div class="text-body-2 font-weight-bold">{{ miembro.membresia }}</div>
              <div class="text-caption text-muted">Válida hasta {{ miembro.fechaFin }}</div>
            </div>
          </v-card>

          <v-alert
            v-if="!puedeRegistrarVisita"
            type="warning"
            variant="tonal"
            density="comfortable"
            border="start"
          >
            Esta membresía no permite registrar la visita. Renueva primero
            para continuar.
          </v-alert>

          <div class="d-flex flex-wrap" style="gap: 12px">
            <v-btn
              v-if="puedeRegistrarVisita"
              color="primary"
              size="large"
              class="flex-grow-1"
              @click="abrirRegistrarVisita"
            >
              <v-icon start>mdi-check</v-icon>
              Registrar visita
            </v-btn>
            <v-btn
              color="warning"
              :variant="puedeRegistrarVisita ? 'outlined' : 'flat'"
              size="large"
              class="flex-grow-1"
              @click="abrirRealizarPago"
            >
              <v-icon start>mdi-wallet-membership</v-icon>
              Renovar membresía
            </v-btn>
          </div>
        </div>
      </v-card>
    </div>

    <v-dialog v-model="mostrarRegistrarVisita" max-width="600px" v-if="miembro">
      <v-card>
        <v-card-title class="text-h5"
          >¿Registrar visita del miembro {{ miembro.nombre }}?</v-card-title
        >
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red darken-1" variant="text" @click="cerrarDialogo"
            >Cancelar
          </v-btn>
          <v-btn
            color="blue darken-1"
            @click="registrar"
            :loading="cargando"
            variant="text"
            :disabled="cargando"
          >
            Registrar visita
            <template v-slot:loader>
              <span>Registrando...</span>
            </template>
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="mostrarRealizarPago" persistent max-width="600">
      <realizar-pago
        :matricula="matricula"
        @cerrar="cerrarDialogoPago"
        @pagado="onPagado"
      />
    </v-dialog>

    <v-snackbar
      v-model="mostrarMensaje"
      :timeout="3000"
      :color="mensaje.color"
      location="top"
    >
      {{ mensaje.texto }}
    </v-snackbar>

    <v-dialog v-model="mostrarDialogoRegular" persistent max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Registrar visita regular</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-text-field
              label="Escribe el pago de la visita"
              v-model="pagoVisita"
              required
            ></v-text-field>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="blue darken-1"
            variant="text"
            @click="mostrarDialogoRegular = false"
          >
            Cerrar
          </v-btn>
          <v-btn
            color="blue darken-1"
            variant="text"
            :loading="cargando"
            :disabled="cargando"
            @click="registrarVisitaRegular"
          >
            Registrar
            <template v-slot:loader>
              <span>Registrando...</span>
            </template>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import BusquedaMiembro from "../Miembros/BusquedaMiembro.vue";
import RealizarPago from "../Miembros/RealizarPago.vue";
import Utiles from "../../Servicios/Utiles";
import HttpService from "../../Servicios/HttpService";

export default {
  name: "RegistrarVisita",
  components: { BusquedaMiembro, RealizarPago },

  data: () => ({
    miembro: null,
    mostrarRegistrarVisita: false,
    mostrarRealizarPago: false,
    mostrarDialogoRegular: false,
    cargando: false,
    mensaje: {},
    mostrarMensaje: false,
    matricula: "",
    pagoVisita: "",
  }),

  computed: {
    estadoNormalizado() {
      if (!this.miembro) return null;
      if (this.miembro.estado === "ACTIVO") return "ACTIVO";
      if (this.miembro.estado === "VENCIDO") return "VENCIDO";
      return "SIN_MEMBRESIA";
    },

    estadoTexto() {
      return (
        {
          ACTIVO: "Activo",
          VENCIDO: "Vencido",
          SIN_MEMBRESIA: "Sin membresía",
        }[this.estadoNormalizado] || ""
      );
    },

    estadoColor() {
      return (
        {
          ACTIVO: "success",
          VENCIDO: "error",
          SIN_MEMBRESIA: "warning",
        }[this.estadoNormalizado] || "warning"
      );
    },

    puedeRegistrarVisita() {
      return this.estadoNormalizado === "ACTIVO";
    },
  },

  methods: {
    registrarVisitaRegular() {
      if (!this.pagoVisita) return;
      this.cargando = true;
      HttpService.registrar(
        {
          metodo: "registrar_regular",
          visita: {
            idUsuario: localStorage.getItem("idUsuario"),
            pago: this.pagoVisita,
          },
        },
        "visitas.php"
      ).then((resultado) => {
        if (resultado) {
          this.cargando = false;
          this.mostrarDialogoRegular = false;
          this.mostrarMensaje = true;
          this.mensaje = {
            texto: "Visita regular registrar",
            color: "success",
          };
        }
      });
    },

    abrirRealizarPago() {
      this.matricula = this.miembro.matricula;
      this.mostrarRealizarPago = true;
    },

    onPagado(resultado) {
      if (resultado) {
        this.mostrarRealizarPago = false;
        this.mostrarMensaje = true;
        this.mensaje = {
          color: "success",
          texto: "Pago realizado con éxito, Ahora puedes registrar la visita",
        };
        this.miembro = null;
      }
    },

    cerrarDialogoPago() {
      this.mostrarRealizarPago = false;
    },

    cerrarDialogo() {
      this.mostrarRegistrarVisita = false;
    },

    registrar() {
      this.cargando = true;
      let payload = {
        metodo: "registrar",
        visita: {
          idMiembro: this.miembro.id,
          idMembresia: this.miembro.idMembresia,
          idUsuario: localStorage.getItem("idUsuario"),
        },
      };

      HttpService.registrar(payload, "visitas.php").then((resultado) => {
        if (resultado) {
          this.mostrarMensaje = true;
          this.mensaje = {
            texto: "Visita registrada",
            color: "success",
          };

          this.miembro = null;
          this.mostrarRegistrarVisita = false;
          this.cargando = false;
        }
      });
    },

    abrirRegistrarVisita() {
      this.mostrarRegistrarVisita = true;
    },

    urlImagen(imagen) {
      return Utiles.generarURL(imagen);
    },

    onMiembroSeleccionado(miembro) {
      this.miembro = miembro;
    },
  },
};
</script>
